import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import Colors from '../../foundations/theme/colors';
// components
import { Row } from '../../foundations/Grid';
import Typography from '../../foundations/Typography';
// styles
import {
  AnchorStyledArticleContainer,
  ArticleStyledDynamicCallout,
  ButtonStyledDynamicCalloutOverlay,
  ColStyledContentContainer,
  ColStyledImageContainer,
  DivStyledBannerImageContainer,
  DivStyledCardImage,
  DivStyledContentContainer,
  DivStyledDynamicCalloutBanner,
  DivStyledDynamicCalloutCard,
  DivStyledDynamicCalloutCardContainer,
  IconStyledVisualLink,
  LinkStyledDynamicCalloutBannerButton,
  SpanStyledArticleFlag,
  TypographyStyledArticleFlag,
  TypographyStyledCard,
  TypographyStyledMetadata,
  TypographyStyledVisualLink,
} from './styles';

export const jsRedirect = (urlPath) => {
  window.location.href = urlPath;
};

export const DynamicCalloutComponent = ({
  actionId,
  assistiveText,
  backgroundVariant = 'lightA',
  branding = 'nm',
  buttonText,
  className,
  disableLazyLoad = false,
  flag,
  image,
  imageWebp,
  isOutgoingLink,
  metadata,
  orientation,
  tag,
  themeType = 'lightTheme',
  title,
  url,
  visualLink,
  ...rest
}) => {
  // Adding appropriate color based on the flag prop. Primary is default.
  const flagPrimary = Colors.sky.sky;
  const flagSecondary = Colors.gold.gold;
  const flagPCG = Colors.brand.pcg.gold2;

  let flagColor;

  if (flag === 'pcg' || branding === 'pcg') {
    flagColor = flagPCG;
  } else if (flag === 'secondary') {
    flagColor = flagSecondary;
  } else {
    flagColor = flagPrimary;
  }

  // card CTA orientation
  if (orientation === 'card') {
    return (
      <DivStyledDynamicCalloutCard
        id={`${actionId}-dynamic-callout-card`}
        className={className}
        {...rest}>
        <ArticleStyledDynamicCallout
          backgroundVariant={backgroundVariant}
          themeType={themeType}>
          <AnchorStyledArticleContainer
            id={actionId}
            href={url}
            data-tracking-id={actionId}
            ariaLabel={assistiveText}
            {...(isOutgoingLink && {
              target: '_blank',
              rel: 'nofollow noopener noreferrer',
            })}>
            {tag && (
              <SpanStyledArticleFlag flagColor={flagColor}>
                <TypographyStyledArticleFlag
                  weight={500}
                  branding={branding}
                  disableBottomPadding>
                  {tag}
                </TypographyStyledArticleFlag>
              </SpanStyledArticleFlag>
            )}
            <DivStyledCardImage
              className={`${!disableLazyLoad && 'lazy'}`}
              image={imageWebp || image}
            />
            <DivStyledDynamicCalloutCardContainer className='js-dynamic-callout-card-content-container'>
              {!!metadata && (
                <TypographyStyledMetadata
                  className='nmx-no-print'
                  backgroundVariant={backgroundVariant}
                  component='time'
                  variant='h6'
                  color={Colors.neutral.grayDark10} // TODO: move to styles
                  weight={300}
                  branding={branding}
                  uppercase>
                  {metadata}
                </TypographyStyledMetadata>
              )}
              <TypographyStyledCard
                component='h3'
                variant='h4'
                backgroundVariant={backgroundVariant}
                color={Colors.neutral.grayDark20} // TODO: move to styles
                branding={branding}
                hasLink={visualLink}>
                {title}
              </TypographyStyledCard>
              {!!visualLink && (
                <TypographyStyledVisualLink
                  className='nmx-no-print'
                  weight={500}
                  backgroundVariant={backgroundVariant}
                  disableBottomPadding>
                  {visualLink}
                  <IconStyledVisualLink />
                </TypographyStyledVisualLink>
              )}
            </DivStyledDynamicCalloutCardContainer>
          </AnchorStyledArticleContainer>
        </ArticleStyledDynamicCallout>
      </DivStyledDynamicCalloutCard>
    );
  }

  return (
    <DivStyledDynamicCalloutBanner
      className={className}
      {...rest}>
      <Row align='center'>
        <ColStyledImageContainer
          small={5}
          medium={6}
          large={5}
          nested>
          <DivStyledBannerImageContainer
            className={`${!disableLazyLoad && 'lazy'}`}
            image={image}
            imageWebp={imageWebp || image}
          />
        </ColStyledImageContainer>
        <ColStyledContentContainer
          small={7}
          medium={6}
          large={7}>
          <DivStyledContentContainer>
            <Typography
              component='h2'
              variant='h3'
              branding={branding}
              color={Colors.neutral.grayDark20} // TODO: move to styles
              disableBottomPadding>
              {title}
            </Typography>
            <LinkStyledDynamicCalloutBannerButton
              id={`${actionId}-button`}
              role='link'
              variant='buttonLinkSecondary'
              data-tracking-id={`${actionId}-button`}
              ariaLabel={assistiveText}
              linkUrl={url}>
              {buttonText || 'Get started'}
            </LinkStyledDynamicCalloutBannerButton>
          </DivStyledContentContainer>
        </ColStyledContentContainer>
      </Row>
      {/* Clickable overlay for the banner variation */}
      <ButtonStyledDynamicCalloutOverlay
        id={actionId}
        role='link'
        data-tracking-id={actionId}
        aria-label={assistiveText} // not Button Component, but button DOM element
        onClick={() => {
          jsRedirect(url); // TODO: this can be a buttonLink
        }}
      />
    </DivStyledDynamicCalloutBanner>
  );
};

DynamicCalloutComponent.propTypes = {
  actionId: PropTypes.string.isRequired,
  assistiveText: PropTypes.string,
  /**  backgroundVariant determines the look and feel of the card */
  backgroundVariant: PropTypes.oneOf(['darkDefault', 'lightA', 'lightB', 'lightC', 'lightSky']),
  /** sets overall branding of component or module, default='nm' */
  branding: PropTypes.oneOf(['nm', 'pcg']),
  buttonText: PropTypes.string,
  /** Optional custom class name */
  className: PropTypes.string,
  disableLazyLoad: PropTypes.bool,
  flag: PropTypes.string, // TODO: flagColor: OneOf: gold, sky, pcg, etc.
  /* the image used on the DynamicCallout */
  image: PropTypes.string.isRequired,
  /* deprecated property, you can still pass this in to override the image, but we recommend just providing the image prop */
  imageWebp: PropTypes.string,
  /** Optional Duration prop (TODO: sync with video DurationStyled naming convention) */
  metadata: PropTypes.string,
  orientation: PropTypes.string,
  isOutgoingLink: PropTypes.bool,
  tag: PropTypes.string,
  /** Optional themeType */
  themeType: PropTypes.oneOf(['lightTheme', 'darkTheme']),
  title: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]).isRequired,
  url: PropTypes.string.isRequired,
  visualLink: PropTypes.string,
};

export default withTheme(DynamicCalloutComponent);
